import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { contactData } from '../data/mock';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Phone, Mail, Instagram, Clock, MapPin } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const ContactSection = () => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const translations = {
    en: {
      title: 'Get In Touch',
      subtitle: 'Have a question or want to place a catering order? Contact us!',
      nameLabel: 'Name',
      emailLabel: 'Email',
      messageLabel: 'Message',
      sendButton: 'Send Message',
      hours: 'Hours',
      contactInfo: 'Contact Information',
      successMessage: 'Message sent successfully!',
      errorMessage: 'Please fill in all fields'
    },
    fr: {
      title: 'Nous Contacter',
      subtitle: 'Une question ou envie de commander pour un événement? Contactez-nous!',
      nameLabel: 'Nom',
      emailLabel: 'Email',
      messageLabel: 'Message',
      sendButton: 'Envoyer le message',
      hours: 'Horaires',
      contactInfo: 'Informations de contact',
      successMessage: 'Message envoyé avec succès!',
      errorMessage: 'Veuillez remplir tous les champs'
    }
  };

  const t = translations[language];
  const hours = language === 'en' ? contactData.hours.en : contactData.hours.fr;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast({
        title: t.errorMessage,
        variant: 'destructive'
      });
      return;
    }
    
    // Mock submission
    toast({
      title: t.successMessage,
      description: language === 'en' ? "We'll get back to you soon!" : "Nous vous répondrons bientôt!"
    });
    
    setFormData({ name: '', email: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-[#0a1929] to-[#0f1f2e]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#c9a961] mb-4">
            {t.title}
          </h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-6">
            <Card className="bg-white/5 border-[#c9a961]/20">
              <CardHeader>
                <CardTitle className="text-[#c9a961] flex items-center space-x-2">
                  <MapPin className="w-5 h-5" />
                  <span>{language === 'en' ? 'Location' : 'Adresse'}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="text-white/80">
                <p>{contactData.address.street}</p>
                <p>{contactData.address.city}, {contactData.address.country} {contactData.address.postalCode}</p>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-[#c9a961]/20">
              <CardHeader>
                <CardTitle className="text-[#c9a961] flex items-center space-x-2">
                  <Clock className="w-5 h-5" />
                  <span>{t.hours}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="text-white/80 space-y-2">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <span className="text-red-400">{language === 'en' ? 'Monday' : 'Lundi'}:</span>
                  <span>{language === 'en' ? hours.monday : hours.monday}</span>
                  
                  <span>{language === 'en' ? 'Tuesday' : 'Mardi'}:</span>
                  <span>{hours.tuesday}</span>
                  
                  <span>{language === 'en' ? 'Wednesday' : 'Mercredi'}:</span>
                  <span>{hours.wednesday}</span>
                  
                  <span>{language === 'en' ? 'Thursday' : 'Jeudi'}:</span>
                  <span>{hours.thursday}</span>
                  
                  <span>{language === 'en' ? 'Friday' : 'Vendredi'}:</span>
                  <span>{hours.friday}</span>
                  
                  <span>{language === 'en' ? 'Saturday' : 'Samedi'}:</span>
                  <span>{hours.saturday}</span>
                  
                  <span>{language === 'en' ? 'Sunday' : 'Dimanche'}:</span>
                  <span>{hours.sunday}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-[#c9a961]/20">
              <CardHeader>
                <CardTitle className="text-[#c9a961]">{t.contactInfo}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <a 
                  href={`tel:${contactData.phone}`} 
                  className="flex items-center space-x-3 text-white/80 hover:text-[#c9a961] transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  <span>{contactData.phone}</span>
                </a>
                <a 
                  href={`mailto:${contactData.email}`} 
                  className="flex items-center space-x-3 text-white/80 hover:text-[#c9a961] transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  <span>{contactData.email}</span>
                </a>
                <a 
                  href={`https://instagram.com/${contactData.instagram}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center space-x-3 text-white/80 hover:text-[#c9a961] transition-colors"
                >
                  <Instagram className="w-5 h-5" />
                  <span>@{contactData.instagram}</span>
                </a>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="bg-white/5 border-[#c9a961]/20">
            <CardHeader>
              <CardTitle className="text-[#c9a961] text-2xl">
                {language === 'en' ? 'Send us a message' : 'Envoyez-nous un message'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-white/80 mb-2">{t.nameLabel}</label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="bg-white/10 border-[#c9a961]/30 text-white placeholder:text-white/40 focus:border-[#c9a961]"
                    placeholder={language === 'en' ? 'Your name' : 'Votre nom'}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-white/80 mb-2">{t.emailLabel}</label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="bg-white/10 border-[#c9a961]/30 text-white placeholder:text-white/40 focus:border-[#c9a961]"
                    placeholder={language === 'en' ? 'your@email.com' : 'votre@email.com'}
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-white/80 mb-2">{t.messageLabel}</label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    className="bg-white/10 border-[#c9a961]/30 text-white placeholder:text-white/40 focus:border-[#c9a961] resize-none"
                    placeholder={language === 'en' ? 'Your message...' : 'Votre message...'}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#c9a961] hover:bg-[#d4b975] text-[#0a1929] font-bold text-lg py-6 transition-all transform hover:scale-105"
                >
                  {t.sendButton}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;